import {
  LinearProgress,
  Tooltip,
  IconButton,
  Icon,
  Stack,
  FormControl,
} from "@mui/material";
import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import MDTypography from "components/MDTypography";
import { centsToDollars } from "global/functions";
import { isEmpty } from "lodash";
import MDBox from "components/MDBox";
import EmptyTableRowOverlay from "components/MDEmptyOverlay/EmptyTableRowOverlay";
import { useState, useEffect } from "react";

import CircularIndeterminate from "components/MDLoading";
import MDSnackbar from "components/MDSnackbar";

import SubscriptionDetail from "./SubscriptionDetails";
import moment from "moment";
import SubscriptionSearchBar from "./SubscriptionSearchBar";
import { capitalizeFirstLetter } from "global/functions";

const SubscriptionsTable = (props) => {
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState("");
  const { subscriptionList, isLoading } = props;
  const [filteredSubscriptions, setFilteredSubscriptions] = useState(
    subscriptionList || []
  );

  // Re-sync filtered list whenever the parent passes a new subscriptionList (date change)
  useEffect(() => {
    setFilteredSubscriptions(subscriptionList || []);
  }, [subscriptionList]);

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  const columnsData = [
    {
      field: "subscriptionNumber",
      headerName: "Subscription ID",
      width: 120,
      editable: false,
      renderCell: ({ row }) => {
        let color;
        switch (row.subscriptionStatus) {
          case "failed":
            color = "red";
            break;
          case "refunded":
            color = "#1b6be4";
            break;
          case "cancel":
            color = "#f5b642";
            break;
        }
        return (
          <MDTypography display="block" variant="caption" style={{ color }}>
            {` ${row.subscriptionNumber || "-"}`}
          </MDTypography>
        );
      },
    },
    {
      field: "subscriptionStatus",
      headerName: "Subscription Status",
      width: 180,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`${capitalizeFirstLetter(row?.subscriptionStatus)}`}
        </MDTypography>
      ),
    },
    {
      field: "parkingCode",
      headerName: "Parking Code",
      width: 180,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`${row?.parkingCode}`}
        </MDTypography>
      ),
    },
    {
      field: "placeAddress",
      headerName: "Place Address",
      width: 220,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          color="text"
          sx={{ whiteSpace: "normal" }}
        >
          {`${row?.placeAddress}`}
        </MDTypography>
      ),
    },
    {
      field: "duration",
      headerName: "Duration",
      width: 180,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography display="block" variant="caption">
          {moment(row.startDate).format("MM/DD/YYYY")} {" - "}
          {moment(row.endDate).format("MM/DD/YYYY")}
        </MDTypography>
      ),
    },

    {
      field: "subscriptionType",
      headerName: "Subscription Type",
      width: 120,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row.isMonthly ? "Monthly" : "Custom"}
        </MDTypography>
      ),
    },

    {
      field: "amount",
      headerName: "Total Amount",
      width: 120,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${centsToDollars(row?.amount)}`}
        </MDTypography>
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 120,

      renderCell: ({ row }) => (
        <>
          <Stack direction="row" alignItems="center" gap={2}>
            <Tooltip title="View Details" placement="top">
              <IconButton
                color="success"
                onClick={() => {
                  setSubscriptionId(row.subscriptionId);
                  setDetailsDialogOpen(true);
                }}
              >
                <Icon fontSize="small">visibility</Icon>
              </IconButton>
            </Tooltip>
          </Stack>
        </>
      ),
    },
  ];

  const getRowClassName = (params) => {
    return params.row.licensePlateGetUpdated ? "updateAvailable" : "";
  };

  return (
    <>
      <MDBox mt={2}>
        <MDBox p={2}>
          <FormControl>
            <SubscriptionSearchBar
              subscriptionsList={subscriptionList}
              setFilteredSubscriptions={setFilteredSubscriptions}
            />
          </FormControl>
        </MDBox>

        <MDDataGrid
          rows={!isEmpty(filteredSubscriptions) ? filteredSubscriptions : []}
          columns={columnsData}
          getRowId={(row) => row._id}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          loading={isLoading}
          components={{
            NoRowsOverlay: EmptyTableRowOverlay,
            LoadingOverlay: LinearProgress,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          // autoHeight
          getRowClassName={getRowClassName}
        />
      </MDBox>

      <SubscriptionDetail
        subscriptionId={subscriptionId}
        dialogOpen={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
        }}
      />

      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait..."
        open={loading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
    </>
  );
};

export default SubscriptionsTable;
